/**
 * citySilhouettes.ts — tiny hand-drawn skyline silhouettes for the city-select
 * cards (inline SVG strings, filled with `currentColor` so each card can tint
 * its own). Bold and minimal on purpose — they're icons, not illustrations,
 * and they're trivially swappable when the Claude Design art arrives.
 */

const VB = `viewBox="0 0 120 44" preserveAspectRatio="xMidYMax meet" aria-hidden="true"`;

const SILHOUETTES: Record<string, string> = {
  // Stepped towers + two spires (Empire / Chrysler energy).
  nyc:
    `<svg ${VB}><path d="M0 40 L0 30 6 30 6 24 12 24 12 40 16 40 16 14 18 8 20 14 20 40 26 40 26 20 34 20 34 40 40 40 40 26 48 26 48 40 54 40 54 6 56 0 58 6 58 40 66 40 66 18 74 18 74 40 82 40 82 28 90 28 90 40 98 40 98 22 106 22 106 40 120 40 120 44 0 44 Z"/></svg>`,
  // Big Ben + Parliament, the Eye across the river, St Paul's dome.
  london:
    `<svg ${VB}><path d="M8 40 V12 L13 4 18 12 V40 Z"/><rect x="20" y="28" width="24" height="12"/>` +
    `<circle cx="68" cy="25" r="13" fill="none" stroke="currentColor" stroke-width="3"/><rect x="66.5" y="36" width="3" height="6"/>` +
    `<path d="M92 40 a13 13 0 0 1 26 0 Z"/><rect x="0" y="40" width="120" height="4"/></svg>`,
  // Vesuvius across the bay, old-town blocks at its feet.
  napoli:
    `<svg ${VB}><path d="M22 40 L46 14 54 19 60 12 90 40 Z"/><circle cx="56" cy="6" r="3" opacity="0.65"/>` +
    `<rect x="2" y="30" width="9" height="10"/><rect x="13" y="33" width="7" height="7"/>` +
    `<rect x="98" y="28" width="8" height="12"/><rect x="108" y="32" width="9" height="8"/><rect x="0" y="40" width="120" height="4"/></svg>`,
  // Tokyo Tower's A-frame over the skyline, Fuji behind.
  tokyo:
    `<svg ${VB}><path d="M14 40 L52 12 90 40 Z" opacity="0.35"/>` +
    `<path d="M28 40 L39 4 43 4 54 40 48 40 41 13 34 40 Z"/><rect x="31" y="27" width="20" height="3"/><rect x="35" y="16" width="12" height="3"/><rect x="40" y="0" width="2.5" height="5"/>` +
    `<rect x="78" y="24" width="11" height="16"/><rect x="94" y="17" width="13" height="23"/><rect x="0" y="40" width="120" height="4"/></svg>`,
  // Sugarloaf's humps + Christ on Corcovado.
  rio:
    `<svg ${VB}><path d="M6 40 C12 16 30 16 36 40 Z"/><path d="M44 40 C48 26 60 26 64 40 Z"/>` +
    `<path d="M76 40 L90 16 104 40 Z"/><rect x="88.5" y="4" width="3" height="12"/><rect x="82" y="7" width="16" height="3"/><rect x="0" y="40" width="120" height="4"/></svg>`,
  // The Renaissance Monument's spike + a flat-topped baobab.
  dakar:
    `<svg ${VB}><path d="M14 40 L24 2 31 40 Z"/><path d="M24 14 L40 26 36 30 Z" opacity="0.8"/>` +
    `<rect x="72" y="26" width="7" height="14"/><ellipse cx="75" cy="19" rx="19" ry="8"/>` +
    `<rect x="100" y="32" width="10" height="8"/><rect x="0" y="40" width="120" height="4"/></svg>`,
  // The cable-stayed Lekki-Ikoyi pylon + Victoria Island towers.
  lagos:
    `<svg ${VB}><path d="M28 40 L35 6 39 6 46 40 41 40 37 14 33 40 Z"/>` +
    `<path d="M37 10 L6 37 M37 10 L20 37 M37 12 L56 37 M37 12 L72 37" stroke="currentColor" stroke-width="2.5" fill="none"/>` +
    `<rect x="0" y="37" width="80" height="3.5"/><rect x="86" y="22" width="11" height="18"/><rect x="101" y="14" width="13" height="26"/><rect x="0" y="40" width="120" height="4"/></svg>`,
  // The Gateway of India arch + the Taj dome beside it.
  mumbai:
    `<svg ${VB}><path d="M10 40 V31 a8 8 0 0 1 16 0 V40 Z"/><circle cx="18" cy="19" r="2.5"/>` +
    `<path d="M42 40 V18 h9 v-5 h6 v5 h10 v-5 h6 v5 h9 V40 h-11 V31 a9 9 0 0 0 -18 0 V40 Z"/>` +
    `<circle cx="49" cy="10" r="3"/><circle cx="75" cy="10" r="3"/><rect x="0" y="40" width="120" height="4"/></svg>`,
  // Fitz Roy's jagged granite teeth.
  patagonia:
    `<svg ${VB}><path d="M48 40 L68 18 82 31 94 21 112 40 Z" opacity="0.45"/>` +
    `<path d="M4 40 L20 10 28 26 38 2 48 24 58 12 72 40 Z"/><rect x="0" y="40" width="120" height="4"/></svg>`,
};

export function citySilhouette(cityId: string): string {
  return SILHOUETTES[cityId] ?? SILHOUETTES.nyc;
}
